import { useTeacherWorkspace, useTeacherUnsavedChanges } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { reportingCyclesApi } from '../../../services/apiV2';

interface Cycle {
  id: number;
  key: string;
  source: 'reporting' | 'instructional';
  title: string;
  start_date: string;
  end_date: string;
  status: string;
}
interface SectionCycles { id: number; name: string; cycles: Cycle[] }

export default function ReportingCycles() {
  const workspace = useTeacherWorkspace();
  const [editing, setEditing] = useState(false);
  const [params] = useSearchParams();
  const [sections, setSections] = useState<SectionCycles[]>([]);
  const [sectionIds, setSectionIds] = useState<number[]>([]);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [editId, setEditId] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useTeacherUnsavedChanges(editing && !!(title || startDate || endDate) && !saving);
  useEffect(() => {
    let cancelled = false;
    reportingCyclesApi.list().then(response => {
      if (cancelled) return;
      const rows: SectionCycles[] = response.data.sections;
      setSections(rows);
      const requested = Number(params.get('section') || params.get('section_id'));
      if (rows.some(row => row.id === requested)) setSectionIds([requested]);
    }).catch(() => { if (!cancelled) setError('Could not load reporting cycles. Reload this page to try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params]);

  const fillForm = (section: SectionCycles, cycle: Cycle, edit: boolean) => {
    setEditId(edit ? cycle.id : undefined);
    setSectionIds([section.id]); setTitle(cycle.title);
    setStartDate(cycle.start_date); setEndDate(cycle.end_date);
    setError(''); setNotice('');
    document.getElementById('reporting-cycle-title')?.focus();
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true); setError(''); setNotice('');
    try {
      await reportingCyclesApi.save({ title, start_date: startDate, end_date: endDate, section_ids: sectionIds }, editId);
      setNotice(editId ? 'Reporting cycle updated.' : 'Reporting dates saved for the selected sections.');
      setEditId(undefined); setTitle(''); setStartDate(''); setEndDate('');
      try {
        const response = await reportingCyclesApi.list();
        setSections(response.data.sections);
      } catch { setError('Dates were saved, but the list could not refresh. Reload this page before making further changes.'); }
    } catch (err: any) {
      const details = err?.response?.data;
      setError(details ? Object.values(details).flat().map(String).join(' ') : 'Could not save reporting dates. Try again.');
    } finally { setSaving(false); }
  };

  return <AppShell role="teacher" title="Reporting Cycles" navItems={teacherV2NavItems('reporting-cycles')}>
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold">Teacher-managed reporting dates</h2>
        <p className="mt-2 text-sm text-muted">Set the dates used by Class Overview, with or without student goals. Apply one set of dates to several sections. This does not create goals or schedule coaching check-ins.</p>
      </div>
      {error && <p role="alert" className="rounded border border-danger p-3 text-danger">{error}</p>}
      {notice && <p role="status" className="rounded border border-success p-3 text-success">{notice}</p>}
      {loading ? <p>Loading reporting cycles…</p> : <>
        <button className="btn-primary" onClick={() => setEditing(!editing)}>Add reporting dates</button>
        {editing && <form onSubmit={save} className="space-y-4 rounded-xl border border-border bg-surface p-4">
          <h3 className="font-semibold">{editId ? 'Edit reporting cycle' : 'Create reporting cycle'}</h3>
          <fieldset disabled={saving} className="space-y-4">
            <label className="block text-sm">Cycle name
              <input id="reporting-cycle-title" required maxLength={120} value={title} onChange={event => setTitle(event.target.value)} placeholder="Cycle 1 — Fall" className="mt-1 block w-full rounded border border-border bg-surface p-2" />
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="text-sm">Start date<input required type="date" value={startDate} onChange={event => setStartDate(event.target.value)} className="mt-1 block rounded border border-border bg-surface p-2" /></label>
              <label className="text-sm">End date<input required type="date" min={startDate || undefined} value={endDate} onChange={event => setEndDate(event.target.value)} className="mt-1 block rounded border border-border bg-surface p-2" /></label>
            </div>
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Sections</legend>
              <div className="flex flex-wrap gap-4">{sections.map(section => <label key={section.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={sectionIds.includes(section.id)} disabled={!!editId} onChange={event => setSectionIds(current => event.target.checked ? [...current, section.id] : current.filter(id => id !== section.id))} />{section.name}
              </label>)}</div>
            </fieldset>
            <p className="text-xs text-muted">Dates are inclusive and use each section’s timezone. Reporting cycles cannot overlap within a section. Current dates are selected automatically; earlier cycles remain available in Class Overview.</p>
            <div className="flex gap-3">
              <button disabled={!sectionIds.length} className="rounded bg-brand px-4 py-2 text-brand-foreground disabled:opacity-50">{saving ? 'Saving…' : editId ? 'Save changes' : 'Save reporting cycle'}</button>
              {editId && <button type="button" onClick={() => { setEditId(undefined); setTitle(''); setStartDate(''); setEndDate(''); }} className="rounded border border-border px-4 py-2">Cancel edit</button>}
            </div>
          </fieldset>
        </form>}
        {sections.length === 0 && <p>No active sections are available.</p>}
        {sections.filter(section => !workspace || String(section.id) === workspace.selected).map(section => <section key={section.id} aria-label={section.name} className="rounded-xl border border-border bg-surface p-4">
          <div className="flex items-center justify-between gap-3"><h3 className="text-lg font-semibold">{section.name}</h3><Link className="text-sm text-brand underline" to={`/v2/teacher?section=${section.id}`}>Class Overview</Link></div>
          {!section.cycles.length && <p className="mt-2 text-sm text-muted">No reporting dates configured. Select this section above to set them.</p>}
          {section.cycles.map(cycle => <div key={cycle.key} className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
            <div><p className="font-medium">{cycle.title} · {cycle.status}</p><p className="text-sm text-muted">{cycle.start_date} to {cycle.end_date} · {cycle.source === 'reporting' ? 'Reporting dates' : 'Existing instructional cycle (reused when no reporting cycle is current)'}</p></div>
            <div className="flex gap-3 text-sm">
              <Link className="text-brand underline" to={`/v2/teacher?section=${section.id}&cycle_key=${encodeURIComponent(cycle.key)}`}>View period</Link>
              <button disabled={saving} className="text-brand underline" onClick={() => { setEditing(true); fillForm(section, cycle, cycle.source === 'reporting'); }}>{cycle.source === 'reporting' ? `Edit ${cycle.title}` : 'Copy these dates'}</button>
            </div>
          </div>)}
        </section>)}
      </>}
    </div>
  </AppShell>;
}
